import { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import Pusher from 'pusher-js';
import useAsync from 'react-use/lib/useAsync';

import { useTenant } from '@packlink/tenant-provider';
import { useSdk } from '@packlink/packlink-sdk-provider';
import { EventHandlerProvider } from '@packlink/event-handler';
import { useGetLanguages, useTranslationProviderStatus } from '@packlink/translation-provider';

import {
    setAvailableLanguages,
    setClientAction,
    setDefaultCountry,
    setLocale,
    setPaymentClientMethodsAction,
    setUserAction,
} from '@store/actions';

import { useLogout } from '@hooks';
import { ErrorBoundary, Footer, Header, Spinner } from '@components';
import { LoadScriptWrapper } from '@components/LoadScriptWrapper/LoadScriptWrapper';

import { SingleFlowRouter } from './SingleFlowRouter';

function useInit() {
    const Packlink = useSdk();
    const dispatch = useDispatch();
    const doLogout = useLogout();
    const getAvailableLanguages = useGetLanguages();
    const { loading: translationsLoading } = useTranslationProviderStatus();

    const [isNotLoggedIn, setIsNotLoggedIn] = useState(false);

    useEffect(() => {
        if (translationsLoading) return;
        getAvailableLanguages((languages) => {
            languages.length && dispatch(setAvailableLanguages(languages));
        });
    }, [dispatch, getAvailableLanguages, translationsLoading]);

    const { loading } = useAsync(
        () =>
            Packlink.pro
                .init()
                .then(({ client, user, paymentMethods }) => {
                    dispatch(setPaymentClientMethodsAction(paymentMethods));
                    dispatch(setUserAction(user));
                    dispatch(setClientAction(client));
                    if (client.language) {
                        dispatch(setLocale(client.language.replace('_', '-')));
                    }
                })
                .catch(() => {
                    setIsNotLoggedIn(true);
                    doLogout();
                }),
        [Packlink, dispatch, doLogout],
    );

    return { loading, isNotLoggedIn };
}

function useDefaultCountry() {
    const tenant = useTenant();
    const dispatch = useDispatch();

    useEffect(() => {
        if (!window.location.hostname.includes('paypal')) {
            dispatch(
                setDefaultCountry(
                    config.defaultCountry[
                        tenant.mapToPlatformCountry().toLocaleLowerCase() as keyof IConfigByPlatformConfig
                    ],
                ),
            );
        }
    }, [dispatch, tenant]);
}

const AppContent = () => {
    const pusherClient = useMemo(
        () => new Pusher(config.pusher.key, { cluster: config.pusher.cluster, forceTLS: true }),
        [],
    );
    return (
        <div className="app__content">
            <ErrorBoundary key="error-boundary">
                <LoadScriptWrapper>
                    <EventHandlerProvider client={pusherClient}>
                        <SingleFlowRouter />
                    </EventHandlerProvider>
                </LoadScriptWrapper>
            </ErrorBoundary>
        </div>
    );
};

const App = (): JSX.Element => {
    useDefaultCountry();
    const { loading, isNotLoggedIn } = useInit();

    return (
        <main className="app">
            <div className="app__header">
                <Header />
            </div>

            {loading || isNotLoggedIn ? <Spinner /> : <AppContent />}
            <div className="app__footer">
                <Footer />
            </div>
        </main>
    );
};

export default App;
